/// <reference path="../../../exported/ajaxUtils.d.ts" />

import React = require("react");
import {CSSProperties} from "react";
import "./LastSignOfLife.scss";
import {defaultTheme} from "@vebox/etui-themes";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../index";

/** Time (in milliseconds) between each update of the last sign of life. */
const LAST_SIGN_OF_LIFE_POLLING_TIME: number = 30 * 1000;
/** Resource to retrieve the last sign of life. */
const LAST_SIGN_OF_LIFE_URL = "getLastSignOfLifeOfEntity";

interface LastSignOfLifeDetailsProps {
    title: string;
    time: string;
    color: string;
}

/**
 * Presentation component for the Equipment/Device details.
 */
const LastSignOfLifeDetails: React.SFC<LastSignOfLifeDetailsProps> = (props) => {
    const valueStyle: CSSProperties = {color: props.color};
    return (
        <div>
            <p className="last-sign-of-life__title">{props.title}</p>
            <p className={"last-sign-of-life__value"}>
                <span style={valueStyle}>{props.time}</span>
            </p>
        </div>
    );
};

interface LastSignOfLifeProps {
    componentId: number;
    title: string;
}

interface LastSignOfLifeState {
    active: boolean;
    time: string;
    color: string;
    lastUpdate: Date;
}

interface ServerRequestParams {
    entityId: number;
    entityType: string;
}

interface ServerResponse {
    time: string;
    color: string;
}

/**
 * Container component to show the last time the device has communicated with the server.
 *
 * Currently it can be used only in the Equipment section, to make it generic:
 *  - Receive the entityType as parameter (or separate it into two components).
 *  - Receive the presentation component as parameter.
 *
 * Improvements:
 *  - The color is currently retrieved from the server. This limits the usage of the components,
 *    the server should only provide a code/priority and the presentation component decides the color.
 */
export class LastSignOfLife extends React.Component<LastSignOfLifeProps, LastSignOfLifeState> {

    constructor(props: LastSignOfLifeProps) {
        super(props);
        this.state = {
            active: true,
            time: null,
            color: "white",
            lastUpdate: null,
        };
        this.retrieveData();
    }

    public componentDidUpdate(prevProps: LastSignOfLifeProps, prevState: LastSignOfLifeState, prevContext: any): void {
        if (this.state.lastUpdate == null || (new Date().getTime() - this.state.lastUpdate.getTime() > 5 * 1000)) {
            // If the component is now active and the last request is old, update the last sign of life immediately.
            this.retrieveData();
        } else {
            setTimeout(() => this.retrieveData(), LAST_SIGN_OF_LIFE_POLLING_TIME);
        }
    }

    public shouldComponentUpdate(
        nextProps: LastSignOfLifeProps,
        nextState: LastSignOfLifeState,
        nextContext: any,
    ): boolean {
        return nextState.active;
    }

    public render() {
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <LastSignOfLifeDetails title={this.props.title} time={this.state.time} color={this.state.color}/>
            </ThemeProvider>
        );
    }

    /**
     * Retrieve the updated last sign of life and change the component state accordingly.
     */
    private retrieveData(): void {
        const params: ServerRequestParams = {entityId: this.props.componentId, entityType: "component"};
        Ajax.vbxAjax({
            url: LAST_SIGN_OF_LIFE_URL,
            data: params,
            type: "GET",
            cache: false,
            success: (response: ServerResponse) => {
                if (response != null && response.time != null) {
                    this.setState({
                        time: response.time,
                        color: (response.color != null ? response.color : "white"),
                        lastUpdate: new Date(),
                    });
                } else {
                    this.setState({lastUpdate: new Date()});
                }
            },
        });
    }
}
